/**
 * @file source-view-utils.ts
 *
 * Foundation utilities for interacting with the CodeMirror 6 source view in
 * markdown mode. Provides two layers:
 *
 *  1. **Context queries** - pure functions over `EditorState` that use the
 *     Lezer syntax tree to answer semantic questions about the current cursor
 *     position / selection (e.g. "is the cursor inside a code block?").
 *
 *  2. **Syntax primitive factories** - functions that return `SourceViewCommand`
 *     values (the standard CM6 command signature) for common markdown text
 *     transformations (e.g. wrapping a selection in `**...**`).
 *
 * These utilities are the shared foundation that toolbar-contributing plugins
 * compose into their own `SourceViewCommand` implementations. All CM6 and
 * Lezer knowledge is centralised here so individual plugins remain ignorant of
 * CodeMirror internals.
 *
 * No React or side-effect imports - this module is pure logic and is safe to
 * import in any context.
 */

import { markdownLanguage } from '@codemirror/lang-markdown';
import { syntaxTree } from '@codemirror/language';
import { EditorSelection } from '@codemirror/state';
import type { EditorState, Text } from '@codemirror/state';
import type { EditorView } from '@codemirror/view';

const ATX_HEADING_REGEX = /^ATXHeading(\d)$/u;
const SETEXT_HEADING_REGEX = /^SetextHeading(\d)$/u;
const TASK_PREFIX_REGEX = /^(\s*)(?:[-*+]|\d+\.)\s+\[[ xX]\]\s+/u;
const LIST_PREFIX_PATTERN_REGEX = /^(\s*)(?:[-*+]|\d+\.)\s+/u;
const LIST_MARKER_REGEX = /^(\s*)([-*+]|\d+\.)\s+/u;
const ORDERED_MARKER_REGEX = /^\d+\.$/u;
const LEADING_WHITESPACE_REGEX = /^\s*/u;
const ATX_HEADING_PREFIX_REGEX = /^(#{1,6}) /u;

// ---------------------------------------------------------------------------
// Lezer node type names
//
// @lezer/markdown does not export its node type names as TypeScript constants -
// they are only accessible at runtime via `node.type.name`. We derive a
// strongly-typed const object from the parser's nodeSet so that:
//   1. The names are guaranteed to match what the parser actually produces.
//   2. Callers get compile-time checking (no raw string literals scattered
//      around the codebase).
//   3. There is a single place to update if the parser ever renames a node.
//
// `markdownLanguage` uses the GFM-extended parser (includes Strikethrough,
// Table, TaskList etc.), which is also what MarkdownSourceView uses.
// ---------------------------------------------------------------------------

/**
 * Strongly-typed map of every Lezer node type name produced by the GFM
 * markdown parser. Values are the exact strings returned by `node.type.name`
 * at runtime. Import individual names from here rather than using raw strings.
 *
 * @example
 * import { LezerNodeTypes } from './source-view-utils';
 * hasAncestor(state, pos, LezerNodeTypes.StrongEmphasis);
 */
export const LezerNodeTypes = Object.fromEntries(
	(
		markdownLanguage.parser as unknown as { nodeSet: { types: { name: string }[] } }
	).nodeSet.types.map((t) => [t.name, t.name]),
) as {
	ATXHeading1: 'ATXHeading1';
	ATXHeading2: 'ATXHeading2';
	ATXHeading3: 'ATXHeading3';
	ATXHeading4: 'ATXHeading4';
	ATXHeading5: 'ATXHeading5';
	ATXHeading6: 'ATXHeading6';
	Blockquote: 'Blockquote';
	BulletList: 'BulletList';
	CodeBlock: 'CodeBlock';
	CodeInfo: 'CodeInfo';
	CodeMark: 'CodeMark';
	// Code content nodes
	CodeText: 'CodeText';
	// Block nodes
	Document: 'Document';
	// Inline nodes
	Emphasis: 'Emphasis';
	// Mark nodes (delimiter characters within inline nodes)
	EmphasisMark: 'EmphasisMark';
	FencedCode: 'FencedCode';
	HeaderMark: 'HeaderMark';
	HorizontalRule: 'HorizontalRule';
	Image: 'Image';
	InlineCode: 'InlineCode';
	Link: 'Link';
	LinkLabel: 'LinkLabel';
	LinkMark: 'LinkMark';
	LinkTitle: 'LinkTitle';
	ListItem: 'ListItem';
	ListMark: 'ListMark';
	OrderedList: 'OrderedList';
	Paragraph: 'Paragraph';
	QuoteMark: 'QuoteMark';
	SetextHeading1: 'SetextHeading1';
	SetextHeading2: 'SetextHeading2';
	Strikethrough: 'Strikethrough';
	StrikethroughMark: 'StrikethroughMark';
	StrongEmphasis: 'StrongEmphasis';
	// GFM extension nodes
	Table: 'Table';
	TableCell: 'TableCell';
	TableDelimiter: 'TableDelimiter';
	TableHeader: 'TableHeader';
	TableRow: 'TableRow';
	Task: 'Task';
	TaskMarker: 'TaskMarker';
	// Link content nodes
	URL: 'URL';
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The standard CM6 command signature. Receives the live `EditorView`, performs
 * a transformation, and returns `true` if the command was handled (even if no
 * document change was required).
 */
export type SourceViewCommand = (view: EditorView) => boolean;

export type SourceViewPositionState = {
	columnNumber: number;
	lineCount: number;
	lineNumber: number;
	/**
	 * Total document word count. Optional so adding it stays a non-breaking,
	 * additive change for external consumers that construct this type; read
	 * sites should default it (e.g. `wordCount ?? 0`).
	 */
	wordCount?: number;
};

// ---------------------------------------------------------------------------
// Word counting
//
// Word count is locale-aware: `@codemirror` has no document word-count API
// (only `EditorState.wordAt`, which returns the word at a single position), so
// we segment the document text with `Intl.Segmenter`. Unlike a naive
// whitespace split, the segmenter correctly counts words in scripts that do
// not delimit words with spaces (e.g. Chinese / Japanese / Thai) and ignores
// punctuation-only segments (so markdown syntax like `**`, `#`, `>` is not
// counted as a word).
//
// The segmenter is created with the editor's runtime locale (the caller threads
// it through from `useIntl().locale` / `getIntl().locale`), so word boundaries
// match the user's language. When no locale is supplied we pass `undefined` to
// `Intl.Segmenter`, which uses the host environment's default locale.
// ---------------------------------------------------------------------------

// Lazily-created, reused segmenters keyed by locale (`undefined` = host default
// locale). Constructing an `Intl.Segmenter` is comparatively expensive, so we
// build one per locale and reuse it. A `null` value means `Intl.Segmenter` is
// genuinely unavailable on this runtime and we should use the whitespace
// fallback.
const wordSegmenters = new Map<string | undefined, Intl.Segmenter | null>();

/**
 * Build an `Intl.Segmenter` for `locale`, or return `null` when the runtime has
 * no `Intl.Segmenter` at all.
 *
 * A malformed `locale` (e.g. a bad `intl.locale` such as `"not a valid locale"`)
 * makes the constructor throw `RangeError`. We must NOT degrade to the
 * whitespace fallback in that case — that miscounts scripts without spaces
 * (CJK / Thai). Instead we retry with the host default locale, which still
 * segments correctly, and emit a one-time diagnostic so the bad locale is
 * observable rather than silently wrong.
 */
function createWordSegmenter(locale: string | undefined): Intl.Segmenter | null {
	if (typeof Intl === 'undefined' || typeof Intl.Segmenter !== 'function') {
		return null;
	}
	try {
		// `undefined` locale → host environment default.
		return new Intl.Segmenter(locale, { granularity: 'word' });
	} catch (err) {
		if (locale !== undefined) {
			// eslint-disable-next-line no-console
			console.warn(
				`[MarkdownSourceView] Intl.Segmenter could not be created for locale "${locale}"; falling back to the host default locale for word counting.`,
				err,
			);
			try {
				return new Intl.Segmenter(undefined, { granularity: 'word' });
			} catch {
				// Host default also failed (very unlikely) — use whitespace split.
			}
		}
		return null;
	}
}

function getWordSegmenter(locale: string | undefined): Intl.Segmenter | null {
	const existing = wordSegmenters.get(locale);
	if (existing !== undefined) {
		return existing;
	}
	// Cache the resolved segmenter (built from `locale` or the host-default
	// fallback) under the requested key so the throwing construction — and its
	// diagnostic — runs at most once per locale.
	const segmenter = createWordSegmenter(locale);
	wordSegmenters.set(locale, segmenter);
	return segmenter;
}

/**
 * Count the number of words in `text` for the given `locale` (or the host
 * default locale when omitted). Prefers `Intl.Segmenter` (locale-aware, counts
 * word-like segments only). Falls back to a whitespace split on runtimes
 * without `Intl.Segmenter`.
 */
function countWordsInText(text: string, locale: string | undefined): number {
	if (text.length === 0) {
		return 0;
	}
	const segmenter = getWordSegmenter(locale);
	if (segmenter) {
		let count = 0;
		for (const segment of segmenter.segment(text)) {
			if (segment.isWordLike) {
				count++;
			}
		}
		return count;
	}
	// Fallback: count whitespace-delimited tokens.
	const matches = text.match(/\S+/gu);
	return matches ? matches.length : 0;
}

// Cache word counts keyed on the immutable CM6 `Text` document instance. CM6
// reuses the same `Text` object when only the selection changes, so this makes
// selection-only updates O(1) and avoids re-scanning the whole document (which
// would add input latency on large documents). A new `Text` instance is
// created on every document edit, so edits recompute exactly once. The cached
// locale is stored alongside the count so a locale change (rare, mid-session)
// correctly invalidates the entry rather than returning a stale count.
const wordCountCache = new WeakMap<Text, { count: number; locale: string | undefined }>();

/**
 * Total word count for the given CM6 document, memoised per `Text` instance.
 *
 * @param doc - The CM6 document to count words in.
 * @param locale - Runtime locale (e.g. from `useIntl().locale`). When omitted,
 * the host environment's default locale is used.
 */
export function countDocumentWords(doc: Text, locale?: string): number {
	const cached = wordCountCache.get(doc);
	if (cached !== undefined && cached.locale === locale) {
		return cached.count;
	}
	const count = countWordsInText(doc.toString(), locale);
	wordCountCache.set(doc, { count, locale });
	return count;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Walk the Lezer syntax tree upward from `pos`, returning the names of every
 * ancestor node (inclusive of the node at `pos`).
 */
function ancestorNames(state: EditorState, pos: number): string[] {
	const names: string[] = [];
	let node = syntaxTree(state).resolveInner(pos, 1);
	while (node.parent) {
		names.push(node.type.name);
		node = node.parent;
	}
	return names;
}

/**
 * Return true if any ancestor node at `pos` has one of the given type names.
 */
function hasAncestor(state: EditorState, pos: number, ...typeNames: string[]): boolean {
	const names = new Set(typeNames);
	let node = syntaxTree(state).resolveInner(pos, 1);
	while (node.parent) {
		if (names.has(node.type.name)) {
			return true;
		}
		node = node.parent;
	}
	return false;
}

/**
 * Return true if any Lezer node overlapping [from, to] has one of the given
 * type names. Uses syntaxTree.iterate() for an exact answer - no sampling.
 */
function rangeOverlapsNodes(
	state: EditorState,
	from: number,
	to: number,
	...typeNames: string[]
): boolean {
	const names = new Set(typeNames);
	let found = false;
	syntaxTree(state).iterate({
		from,
		to,
		enter(node) {
			if (names.has(node.type.name)) {
				found = true;
				return false; // stop iteration early
			}
		},
	});
	return found;
}

// ---------------------------------------------------------------------------
// Context queries - pure functions over EditorState
// ---------------------------------------------------------------------------

/**
 * The supported inline format types. Each maps to a canonical Lezer node type
 * and a canonical syntax string used when toggling on (wrapping).
 *
 * Using this union instead of free (syntax, lezerNodeType) pair arguments
 * ensures the two cannot be accidentally mismatched.
 */
export type SourceViewInlineFormat = 'bold' | 'italic' | 'inlineCode' | 'strikethrough';

/** Canonical wrap syntax for each inline format (used for toggle-on). */
const INLINE_FORMAT_SYNTAX: Record<SourceViewInlineFormat, string> = {
	bold: '**',
	italic: '*',
	inlineCode: '`',
	strikethrough: '~~',
};

/** Lezer node type for each inline format (used for toggle-off detection). */
const INLINE_FORMAT_NODE_TYPE: Record<SourceViewInlineFormat, keyof typeof LezerNodeTypes> = {
	bold: 'StrongEmphasis',
	italic: 'Emphasis',
	inlineCode: 'InlineCode',
	strikethrough: 'Strikethrough',
};

/**
 * The Lezer node types that represent "code contexts" - positions where inline
 * markdown formatting (bold, italic, etc.) is meaningless or invalid.
 */
const CODE_CONTEXT_NODES: (keyof typeof LezerNodeTypes)[] = [
	'FencedCode',
	'CodeBlock',
	'InlineCode',
	'CodeText', // interior of InlineCode
	'CodeInfo', // language tag on a fenced block
];

/**
 * Returns true if the primary selection's head is inside a code context
 * (fenced code block, indented code block, or inline code span). Formatting
 * commands should be disabled when this is true.
 */
export function isInCodeContext(state: EditorState): boolean {
	const { head } = state.selection.main;
	return hasAncestor(state, head, ...CODE_CONTEXT_NODES);
}

/**
 * Returns true if the primary selection overlaps any code context node
 * (fenced code block, indented code block, or inline code span). This is
 * exact - it walks the Lezer tree for the full selection range rather than
 * sampling positions. Use this when you need to check a range selection, e.g.
 * to disable a command when part of the selection is inside code.
 *
 * For a collapsed cursor, prefer `isInCodeContext` which checks the cursor
 * head position directly.
 */
export function isSelectionInCodeContext(state: EditorState): boolean {
	const { from, to } = state.selection.main;
	// For a collapsed cursor, use the ancestor walk (faster).
	if (from === to) {
		return hasAncestor(state, from, ...CODE_CONTEXT_NODES);
	}
	return rangeOverlapsNodes(state, from, to, ...CODE_CONTEXT_NODES);
}

function isSelectionInBlockCodeContext(state: EditorState): boolean {
	const { from, to } = state.selection.main;
	if (from === to) {
		return hasAncestor(state, from, LezerNodeTypes.FencedCode, LezerNodeTypes.CodeBlock);
	}
	return rangeOverlapsNodes(state, from, to, LezerNodeTypes.FencedCode, LezerNodeTypes.CodeBlock);
}

/**
 * Returns true if the cursor/selection is inside a link URL (the `(…)` part
 * of `[text](url)`). Formatting inside a URL is meaningless.
 */
export function isInLinkUrl(state: EditorState): boolean {
	const { head } = state.selection.main;
	return hasAncestor(state, head, LezerNodeTypes.URL);
}

/**
 * Returns the 1-based cursor line/column and total document line count for the
 * current source-view selection. These are cheap to compute (O(log n) lookups)
 * and safe to recompute on every keystroke/selection change.
 *
 * Excludes the word count, which is comparatively expensive (a full-document
 * scan) — see `countDocumentWords` / `computeSourceViewPositionState`.
 */
export function computeSourceViewCursorPosition(
	state: EditorState,
): Omit<SourceViewPositionState, 'wordCount'> {
	const { head } = state.selection.main;
	const line = state.doc.lineAt(head);

	return {
		columnNumber: head - line.from + 1,
		lineCount: state.doc.lines,
		lineNumber: line.number,
	};
}

/**
 * Returns 1-based cursor line/column, total document line count, and total
 * document word count for the current source-view selection.
 *
 * The word count is memoised per document instance (see `countDocumentWords`),
 * so selection-only updates do not re-scan the document. Because the word count
 * is a full-document scan, callers that update on every keystroke should debounce
 * it — see `MarkdownSourceView`, which recomputes the cursor fields immediately
 * (`computeSourceViewCursorPosition`) but the word count only after a pause.
 *
 * @param state - The CM6 editor state to read the selection/document from.
 * @param locale - Runtime locale (e.g. from `useIntl().locale`) used for
 * locale-aware word segmentation. Uses the host default locale when omitted.
 */
export function computeSourceViewPositionState(
	state: EditorState,
	locale?: string,
): SourceViewPositionState {
	return {
		...computeSourceViewCursorPosition(state),
		wordCount: countDocumentWords(state.doc, locale),
	};
}

/**
 * Returns true if inline formatting commands (bold, italic, code, strike) are
 * available at the current cursor position. False when inside a code block or
 * link URL where formatting syntax has no effect.
 */
export function isInlineFormattingAvailable(state: EditorState): boolean {
	return !isInCodeContext(state) && !isInLinkUrl(state);
}

/**
 * Returns true if the cursor is currently inside a node of the given Lezer
 * type name. Use this to implement `isActive` checks.
 *
 * @example
 * isFormatActiveAt(state, 'StrongEmphasis') // bold
 * isFormatActiveAt(state, 'Emphasis')       // italic
 * isFormatActiveAt(state, 'InlineCode')     // inline code
 * isFormatActiveAt(state, 'Strikethrough')  // strikethrough
 */
export function isFormatActiveAt(
	state: EditorState,
	lezerNodeType: keyof typeof LezerNodeTypes,
): boolean {
	const { head } = state.selection.main;
	return hasAncestor(state, head, lezerNodeType);
}

/**
 * Returns the heading level (1–6) if the cursor is inside an ATX or setext
 * heading node, or `null` if it is not.
 */
export function getHeadingLevelAt(state: EditorState): number | null {
	// Helper: extract heading level from a list of ancestor node names.
	function levelFromNames(names: string[]): number | null {
		for (const name of names) {
			const atx = name.match(ATX_HEADING_REGEX);
			if (atx) {
				return parseInt(atx[1], 10);
			}
			const setext = name.match(SETEXT_HEADING_REGEX);
			if (setext) {
				return parseInt(setext[1], 10);
			}
		}
		return null;
	}

	const { head } = state.selection.main;

	// First try the cursor position directly.
	const direct = levelFromNames(ancestorNames(state, head));
	if (direct !== null) {
		return direct;
	}

	// When the cursor is at the very end of a line (on the \n character),
	// Lezer places it outside the ATXHeading node boundary. Fall back to
	// the last character of the line to detect the heading correctly.
	const line = state.doc.lineAt(head);
	if (head === line.to && line.to > line.from) {
		return levelFromNames(ancestorNames(state, line.to - 1));
	}

	return null;
}

/**
 * Returns true if the primary selection head is inside `typeName`, including
 * the common CM6 edge case where the cursor sits at the end of a non-empty
 * line and Lezer resolves it onto the trailing newline outside the node.
 */
function isAtCursorOrLineEnd(state: EditorState, typeName: keyof typeof LezerNodeTypes): boolean {
	const { head } = state.selection.main;

	if (hasAncestor(state, head, typeName)) {
		return true;
	}

	const line = state.doc.lineAt(head);
	return head === line.to && line.to > line.from && hasAncestor(state, line.to - 1, typeName);
}

/**
 * Returns true if the cursor is inside a blockquote.
 */
export function isInBlockquote(state: EditorState): boolean {
	return isAtCursorOrLineEnd(state, LezerNodeTypes.Blockquote);
}

/**
 * Returns true if the cursor is inside a bullet (unordered) list item.
 */
export function isInBulletList(state: EditorState): boolean {
	return isAtCursorOrLineEnd(state, LezerNodeTypes.BulletList);
}

/**
 * Returns true if the cursor is inside an ordered list item.
 */
export function isInOrderedList(state: EditorState): boolean {
	return isAtCursorOrLineEnd(state, LezerNodeTypes.OrderedList);
}

/**
 * Returns true if the cursor is inside a GFM task list item.
 */
export function isInTaskList(state: EditorState): boolean {
	return (
		isAtCursorOrLineEnd(state, LezerNodeTypes.Task) ||
		isAtCursorOrLineEnd(state, LezerNodeTypes.TaskMarker)
	);
}

/**
 * Returns true if the cursor is currently in an active state for the given
 * inline format. Convenience wrapper over `isFormatActiveAt` using the
 * `SourceViewInlineFormat` enum.
 */
export function isInlineFormatActive(state: EditorState, format: SourceViewInlineFormat): boolean {
	return isFormatActiveAt(state, INLINE_FORMAT_NODE_TYPE[format]);
}

// ---------------------------------------------------------------------------
// Format state snapshot - a single object summarising the toolbar state
// ---------------------------------------------------------------------------

/**
 * Inline formatting state - subscribed to by `editor-plugin-text-formatting`.
 * Contains only the fields that inline formatting toolbar buttons care about.
 */
export type SourceViewInlineFormatState = {
	boldActive: boolean;
	codeActive: boolean;
	/** False when cursor is in a code block or link URL - disables all inline format buttons. */
	inlineFormattingAvailable: boolean;
	italicActive: boolean;
	strikeActive: boolean;
};

/**
 * Block format state - subscribed to by `editor-plugin-block-type`.
 * Contains heading level and block-context flags.
 */
export type SourceViewBlockFormatState = {
	/** 1–6 when cursor is inside an ATX/setext heading, null otherwise. */
	headingLevel: number | null;
	inBlockquote: boolean;
	/** True when cursor is inside a fenced or indented code block. */
	inCodeBlock: boolean;
};

/**
 * List format state - subscribed to by `editor-plugin-list` (future).
 */
export type SourceViewListFormatState = {
	inBulletList: boolean;
	inOrderedList: boolean;
	inTaskList: boolean;
};

export type SourceViewFormatState = {
	block: SourceViewBlockFormatState;
	inline: SourceViewInlineFormatState;
	list: SourceViewListFormatState;
};

/**
 * Compute the inline format state for the current CM6 editor state.
 * Called from `MarkdownSourceView`'s `updateListener` on every selection or
 * document change.
 */
export function computeSourceViewInlineFormatState(
	state: EditorState,
): SourceViewInlineFormatState {
	return {
		boldActive: isInlineFormatActive(state, 'bold'),
		italicActive: isInlineFormatActive(state, 'italic'),
		codeActive: isInlineFormatActive(state, 'inlineCode'),
		strikeActive: isInlineFormatActive(state, 'strikethrough'),
		inlineFormattingAvailable: isInlineFormattingAvailable(state),
	};
}

/**
 * Compute the block format state for the current CM6 editor state.
 * Called from `MarkdownSourceView`'s `updateListener` on every selection or
 * document change.
 */
export function computeSourceViewBlockFormatState(state: EditorState): SourceViewBlockFormatState {
	const { head } = state.selection.main;

	return {
		headingLevel: getHeadingLevelAt(state),
		inBlockquote: isInBlockquote(state),
		inCodeBlock: hasAncestor(state, head, LezerNodeTypes.FencedCode, LezerNodeTypes.CodeBlock),
	};
}

/**
 * Compute the list format state for the current CM6 editor state.
 * Called from `MarkdownSourceView`'s `updateListener` on every selection or
 * document change.
 */
export function computeSourceViewListFormatState(state: EditorState): SourceViewListFormatState {
	// Derive list type from the actual text prefix on the cursor's line.
	// This is the ground truth — whichever marker appears first on the line
	// determines the list type, avoiding ambiguity from Lezer ancestor checks
	// that can overlap (e.g. task list nodes live inside BulletList nodes).
	//
	// Regex groups:
	//   TASK_PREFIX_REGEX   — bullet/ordered marker followed by `[ ]` checkbox
	//   LIST_MARKER_REGEX — any list marker; group 2 distinguishes type:
	//     \d+\. → ordered list
	//     [-*+]  → bullet list
	const { head } = state.selection.main;
	const lineText = state.doc.lineAt(head).text;

	if (TASK_PREFIX_REGEX.test(lineText)) {
		return { inBulletList: false, inOrderedList: false, inTaskList: true };
	}

	const listMatch = lineText.match(LIST_MARKER_REGEX);
	if (listMatch) {
		const marker = listMatch[2];
		const isOrdered = ORDERED_MARKER_REGEX.test(marker);
		return {
			inBulletList: !isOrdered,
			inOrderedList: isOrdered,
			inTaskList: false,
		};
	}

	return { inBulletList: false, inOrderedList: false, inTaskList: false };
}

// ---------------------------------------------------------------------------
// Syntax primitive factories - return SourceViewCommand values
// ---------------------------------------------------------------------------

/**
 * The Lezer mark node types used to delimit inline formatting. These are the
 * nodes whose text we remove when toggling off a format.
 */
const INLINE_MARK_NODE_TYPES = new Set<string>([
	LezerNodeTypes.EmphasisMark,
	LezerNodeTypes.StrikethroughMark,
	LezerNodeTypes.CodeMark,
]);

/**
 * Find the innermost enclosing Lezer node of `nodeType` at `pos`, then return
 * the positions of its opening and closing mark child nodes. Returns null if
 * no such node is found or if the marks cannot be located.
 */
function findEnclosingMarkPositions(
	state: EditorState,
	pos: number,
	nodeType: keyof typeof LezerNodeTypes,
): { closeFrom: number; closeTo: number; openFrom: number; openTo: number } | null {
	let node = syntaxTree(state).resolveInner(pos, 1);
	while (node.parent && node.type.name !== nodeType) {
		node = node.parent;
	}
	if (node.type.name !== nodeType) {
		return null;
	}

	let openMark = node.firstChild;
	while (openMark && !INLINE_MARK_NODE_TYPES.has(openMark.type.name)) {
		openMark = openMark.nextSibling;
	}
	let closeMark = node.lastChild;
	while (closeMark && !INLINE_MARK_NODE_TYPES.has(closeMark.type.name)) {
		closeMark = closeMark.prevSibling;
	}

	if (!openMark || !closeMark || openMark === closeMark) {
		return null;
	}

	return {
		openFrom: openMark.from,
		openTo: openMark.to,
		closeFrom: closeMark.from,
		closeTo: closeMark.to,
	};
}

/**
 * Returns a `SourceViewCommand` that wraps the current selection in the
 * canonical syntax for `format` (e.g. `**` for bold, `_` for italic).
 *
 * - Toggle off: uses the Lezer tree to find and remove the actual delimiter
 *   marks - handles all syntax variants (** or __ for bold, * or _ for italic) and combined marks (e.g. *** for bold+italic).
 * - No selection: inserts the syntax pair and places the cursor between them.
 * - Toggle on: wraps the selection in the canonical syntax.
 * - Returns false when inline formatting is unavailable (code block, link URL),
 *   except for 'inlineCode' which is always available.
 */
export function toggleWrapWithSyntax(format: SourceViewInlineFormat): SourceViewCommand {
	return (view: EditorView): boolean => {
		const { state } = view;
		const syntax = INLINE_FORMAT_SYNTAX[format];
		const lezerNodeType = INLINE_FORMAT_NODE_TYPE[format];

		if (format !== 'inlineCode' && (isSelectionInCodeContext(state) || isInLinkUrl(state))) {
			return false;
		}

		const range = state.selection.main;
		const { from, to } = range;

		// Toggle off via Lezer tree - variant-agnostic, handles combined marks.
		const marks = findEnclosingMarkPositions(state, range.head, lezerNodeType);
		if (marks) {
			const { openFrom, openTo, closeFrom, closeTo } = marks;
			const changes = [
				{ from: openFrom, to: openTo, insert: '' },
				{ from: closeFrom, to: closeTo, insert: '' },
			];
			const changeSet = state.changes(changes);
			const mappedSelection = EditorSelection.create(
				state.selection.ranges.map((range) => range.map(changeSet, 1)),
				state.selection.mainIndex,
			);
			view.dispatch(
				state.update({
					changes,
					selection: mappedSelection,
				}),
			);
			return true;
		}

		// No selection - insert pair and place cursor between.
		if (range.empty) {
			view.dispatch(
				state.update({
					changes: { from, insert: syntax + syntax },
					selection: { anchor: from + syntax.length },
				}),
			);
			return true;
		}

		// Toggle on - wrap selection.
		view.dispatch(
			state.update({
				changes: [
					{ from, insert: syntax },
					{ from: to, insert: syntax },
				],
				selection: {
					anchor: from + syntax.length,
					head: to + syntax.length,
				},
			}),
		);
		return true;
	};
}

/**
 * Returns a `SourceViewCommand` that prefixes the line(s) containing the
 * current selection with `prefix`. If all selected lines already have the
 * prefix, it is removed (toggle off). Lines in a multi-line selection that
 * already have the prefix are left unchanged when toggling on.
 * Returns false inside fenced code blocks.
 */
export function togglePrefixLines(
	prefix: string,
	options: { stripConflictingList?: boolean } = {},
): SourceViewCommand {
	const { stripConflictingList = false } = options;
	return (view: EditorView): boolean => {
		const { state } = view;
		const { from, to } = state.selection.main;

		if (isSelectionInBlockCodeContext(state)) {
			return false;
		}

		const doc = state.doc;
		const firstLine = doc.lineAt(from);
		const lastLine = doc.lineAt(to === from ? from : to - 1);

		const allHavePrefix = Array.from({ length: lastLine.number - firstLine.number + 1 }, (_, i) =>
			doc.line(firstLine.number + i),
		).every((line) => line.text.startsWith(prefix));

		const changes: { from: number; insert: string; to?: number }[] = [];
		// When inserting a prefix on a blank line with a trailing '\n' to preserve
		// CommonMark block separation, we need to explicitly place the cursor after
		// the prefix (not after the '\n'). Track that position here.
		let explicitCursorPos: number | null = null;

		for (let n = firstLine.number; n <= lastLine.number; n++) {
			const line = doc.line(n);
			if (allHavePrefix) {
				changes.push({ from: line.from, to: line.from + prefix.length, insert: '' });
			} else if (!line.text.startsWith(prefix)) {
				// When switching list types, strip any existing list prefix before
				// inserting the new one so that bullet and ordered lists remain
				// mutually exclusive (fixes: both list types active simultaneously).
				if (stripConflictingList) {
					const existingListMatch = line.text.match(LIST_MARKER_REGEX);
					if (existingListMatch) {
						changes.push({
							from: line.from,
							to: line.from + existingListMatch[0].length,
							insert: prefix,
						});
						continue;
					}
				}
				if (line.text === '' && n === firstLine.number && n === lastLine.number) {
					// Inserting a list/quote prefix on a blank line between paragraphs.
					// Without a trailing blank line, the next paragraph would be parsed
					// as a lazy continuation of this list item by CommonMark. Insert
					// a blank line after the prefix to keep the blocks separate.
					const nextLine = n < doc.lines ? doc.line(n + 1) : null;
					if (nextLine && nextLine.text !== '') {
						changes.push({ from: line.from, insert: prefix + '\n' });
						// Cursor should land after the prefix, not after the inserted '\n'.
						explicitCursorPos = line.from + prefix.length;
					} else {
						changes.push({ from: line.from, insert: prefix });
					}
				} else {
					changes.push({ from: line.from, insert: prefix });
				}
			}
		}

		const changeSet = state.changes(changes);

		// If we inserted "prefix\n" on a blank line, place the cursor explicitly
		// after the prefix so it stays on the same (now prefixed) line.
		// Otherwise, map the selection through the changes. Use assoc=1 so that positions
		// that coincide with an insertion point (e.g. cursor at col 0 when we
		// insert a prefix there) are moved forward rather than left behind.
		const mappedSelection =
			explicitCursorPos !== null
				? EditorSelection.cursor(explicitCursorPos)
				: EditorSelection.create(
						state.selection.ranges.map((range) => range.map(changeSet, 1)),
						state.selection.mainIndex,
					);
		const tr = state.update({ changes, selection: mappedSelection });

		view.dispatch(tr);
		return true;
	};
}

/**
 * Toggles GFM task-list syntax (`- [ ] `) on the selected line(s).
 * Existing unordered/ordered list markers are converted to task markers.
 */
export function toggleTaskListSyntax(): SourceViewCommand {
	return (view: EditorView): boolean => {
		const { state } = view;
		const { from, to } = state.selection.main;

		if (isSelectionInBlockCodeContext(state)) {
			return false;
		}

		const doc = state.doc;
		const firstLine = doc.lineAt(from);
		const lastLine = doc.lineAt(to === from ? from : to - 1);
		const lines = Array.from({ length: lastLine.number - firstLine.number + 1 }, (_, i) =>
			doc.line(firstLine.number + i),
		);
		const allHaveTaskPrefix = lines.every((line) => TASK_PREFIX_REGEX.test(line.text));

		const changes: { from: number; insert: string; to?: number }[] = [];

		for (const line of lines) {
			const taskMatch = line.text.match(TASK_PREFIX_REGEX);
			if (allHaveTaskPrefix && taskMatch) {
				changes.push({
					from: line.from + taskMatch[1].length,
					to: line.from + taskMatch[0].length,
					insert: '',
				});
				continue;
			}
			if (taskMatch) {
				continue;
			}

			const listMatch = line.text.match(LIST_PREFIX_PATTERN_REGEX);
			if (listMatch) {
				changes.push({
					from: line.from + listMatch[1].length,
					to: line.from + listMatch[0].length,
					insert: '- [ ] ',
				});
				continue;
			}

			const indentation = line.text.match(LEADING_WHITESPACE_REGEX)?.[0] ?? '';
			if (line.text === '' && line.number === firstLine.number && line.number === lastLine.number) {
				const nextLine = line.number < doc.lines ? doc.line(line.number + 1) : null;
				changes.push({
					from: line.from,
					insert: nextLine && nextLine.text !== '' ? '- [ ] \n' : '- [ ] ',
				});
			} else {
				changes.push({ from: line.from + indentation.length, insert: '- [ ] ' });
			}
		}

		const changeSet = state.changes(changes);
		const mappedSelection = EditorSelection.create(
			state.selection.ranges.map((range) => range.map(changeSet, 1)),
			state.selection.mainIndex,
		);

		view.dispatch(state.update({ changes, selection: mappedSelection }));
		return true;
	};
}

/**
 * Returns a `SourceViewCommand` that sets or removes the ATX heading prefix
 * for `level` on the current line. Toggles off if the same level is applied,
 * replaces if a different level, adds if not a heading. Returns false inside
 * fenced code blocks.
 */
export function toggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6): SourceViewCommand {
	return (view: EditorView): boolean => {
		const { state } = view;
		const { head } = state.selection.main;

		if (hasAncestor(state, head, LezerNodeTypes.FencedCode, LezerNodeTypes.CodeBlock)) {
			return false;
		}

		const line = state.doc.lineAt(head);
		const text = line.text;
		const existingMatch = text.match(ATX_HEADING_PREFIX_REGEX);
		const targetPrefix = '#'.repeat(level) + ' ';
		const existingHeadingLevel = getHeadingLevelAt(state);

		if (existingMatch) {
			const existingPrefix = existingMatch[0];
			if (existingPrefix === targetPrefix) {
				view.dispatch(
					state.update({
						changes: { from: line.from, to: line.from + existingPrefix.length, insert: '' },
					}),
				);
			} else {
				view.dispatch(
					state.update({
						changes: {
							from: line.from,
							to: line.from + existingPrefix.length,
							insert: targetPrefix,
						},
					}),
				);
			}
		} else if (existingHeadingLevel !== null) {
			return false;
		} else {
			view.dispatch(
				state.update({
					changes: { from: line.from, insert: targetPrefix },
				}),
			);
		}

		return true;
	};
}

/**
 * Returns a `SourceViewCommand` that wraps the current line/selection in a
 * fenced code block. If the cursor is already inside a fenced code block, the
 * fences are removed.
 */
export function toggleFencedCodeBlock(): SourceViewCommand {
	return (view: EditorView): boolean => {
		const { state } = view;
		const { from, to } = state.selection.main;

		if (hasAncestor(state, from, LezerNodeTypes.FencedCode)) {
			let node = syntaxTree(state).resolveInner(from, 1);
			while (node.parent && node.type.name !== LezerNodeTypes.FencedCode) {
				node = node.parent;
			}
			if (node.type.name === LezerNodeTypes.FencedCode) {
				const fenceText = state.doc.sliceString(node.from, node.to);
				const openFenceEnd = fenceText.indexOf('\n');
				const closeFenceStart = fenceText.lastIndexOf('\n');
				if (openFenceEnd !== -1) {
					const inner = fenceText.slice(openFenceEnd + 1, closeFenceStart);
					view.dispatch(
						state.update({
							changes: { from: node.from, to: node.to, insert: inner },
						}),
					);
					return true;
				}
			}
		}

		if (isSelectionInBlockCodeContext(state)) {
			return false;
		}

		const doc = state.doc;
		const firstLine = doc.lineAt(from);
		const lastLine = doc.lineAt(to === from ? from : to - 1);
		const selectedText = doc.sliceString(firstLine.from, lastLine.to);
		view.dispatch(
			state.update({
				changes: {
					from: firstLine.from,
					to: lastLine.to,
					insert: '```\n' + selectedText + '\n```',
				},
				selection: { anchor: firstLine.from + 4 },
			}),
		);
		return true;
	};
}

// ---------------------------------------------------------------------------
// Pre-built commands for common formatting actions
// (compose the factories above into ready-to-use SourceViewCommand values)
// ---------------------------------------------------------------------------

/** Bold: wrap/unwrap selection in `**...**` */
export const boldSourceViewCommand: SourceViewCommand = toggleWrapWithSyntax('bold');

/** Italic: wrap/unwrap selection in `_..._` */
export const italicSourceViewCommand: SourceViewCommand = toggleWrapWithSyntax('italic');

/** Inline code: wrap/unwrap selection in `` `...` `` */
export const inlineCodeSourceViewCommand: SourceViewCommand = toggleWrapWithSyntax('inlineCode');

/** Strikethrough: wrap/unwrap selection in `~~...~~` */
export const strikeSourceViewCommand: SourceViewCommand = toggleWrapWithSyntax('strikethrough');

/** Blockquote: prefix/unprefixed lines with `> ` */
export const blockquoteSourceViewCommand: SourceViewCommand = togglePrefixLines('> ');

/** Bullet list: prefix/unprefixed lines with `* ` (matches ADF serialisation default) */
export const bulletListSourceViewCommand: SourceViewCommand = togglePrefixLines('* ', {
	stripConflictingList: true,
});

/** Ordered list: prefix/unprefixed lines with `1. ` */
export const orderedListSourceViewCommand: SourceViewCommand = togglePrefixLines('1. ', {
	stripConflictingList: true,
});

/** Task list: toggle GFM task-list syntax (`- [ ] `) */
export const taskListSourceViewCommand: SourceViewCommand = toggleTaskListSyntax();

/** Heading level 1–6 */
export const heading1SourceViewCommand: SourceViewCommand = toggleHeading(1);
export const heading2SourceViewCommand: SourceViewCommand = toggleHeading(2);
export const heading3SourceViewCommand: SourceViewCommand = toggleHeading(3);
export const heading4SourceViewCommand: SourceViewCommand = toggleHeading(4);
export const heading5SourceViewCommand: SourceViewCommand = toggleHeading(5);
export const heading6SourceViewCommand: SourceViewCommand = toggleHeading(6);

/** Fenced code block */
export const codeBlockSourceViewCommand: SourceViewCommand = toggleFencedCodeBlock();

/**
 * Builds a markdown table string with the given number of rows and columns.
 * Row count includes the header row (so rowsCount=3 → 1 header + 2 data rows).
 * Used by `insertSourceTableCommand` to insert tables at the CM cursor.
 */
function buildMarkdownTable(rowsCount: number, colsCount: number): string {
	const emptyRow = `| ${Array.from({ length: colsCount }, () => ' ').join(' | ')} |`;
	const separator = `| ${Array.from({ length: colsCount }, () => '-').join(' | ')} |`;
	const dataRows = Array.from({ length: rowsCount - 1 }, () => emptyRow);
	return [emptyRow, separator, ...dataRows].join('\n');
}

/**
 * Returns a `SourceViewCommand` that inserts a markdown table at the CM cursor.
 * Inserts `rowsCount` rows (including header) and `colsCount` columns.
 */
export function insertSourceTableCommand(
	rowsCount: number = 3,
	colsCount: number = 3,
): SourceViewCommand {
	return (view) => {
		const markdownTable = buildMarkdownTable(rowsCount, colsCount);
		const { from, to } = view.state.selection.main;
		view.dispatch(
			view.state.update({
				changes: { from, to, insert: markdownTable },
				selection: { anchor: from + markdownTable.length },
			}),
		);
		return true;
	};
}
